/**
 * Tab Stack Renderers - Telegram App
 * Maps screen names to components. ProfileScreen is app-specific,
 * all other screens are imported from @procoach/shared.
 */

import type { TabStackEntry } from '@shared/types/domain';

// Home tab screens
import { HomeScreen } from '@shared/screens/Home/HomeScreen';
import { AssistantDetailScreen } from '@shared/screens/Home/AssistantDetailScreen';
import { EventListScreen } from '@shared/screens/Events/EventListScreen';
import { EventDetailScreen } from '@shared/screens/Events/EventDetailScreen';
import { CalendarScreen } from '@shared/screens/Calendar/CalendarScreen';
import { AttendanceScreen } from '@shared/screens/Attendance/AttendanceScreen';
import MaterialsScreen from '@shared/screens/Materials/MaterialsScreen';

// Athletes tab screens
import { AthletesScreen } from '@shared/screens/Athletes/AthletesScreen';
import { AthleteDetailScreen } from '@shared/screens/Athletes/AthleteDetailScreen';
import { ImportAthletesScreen } from '@shared/screens/Athletes/ImportAthletesScreen';

// Groups tab screens
import { GroupsScreen } from '@shared/screens/Groups/GroupsScreen';
import { GroupDetailScreen } from '@shared/screens/Groups/GroupDetailScreen';
import { CoachListScreen } from '@shared/screens/Coaches/CoachListScreen';
import { CoachDetailScreen } from '@shared/screens/Coaches/CoachDetailScreen';

// Profile tab screens (ProfileScreen is app-specific)
import { ProfileScreen } from '../screens/Profile/ProfileScreen';
import { FeedbackScreen } from '@shared/screens/Profile/FeedbackScreen';
import { SubscriptionManageScreen } from '@shared/screens/Subscription/SubscriptionManageScreen';
import { SubscriptionPlansScreen } from '@shared/screens/Subscription/SubscriptionPlansScreen';
import { TierChangeConfirmScreen } from '@shared/screens/Subscription/TierChangeConfirmScreen';
import { SubscriptionHelpScreen } from '@shared/screens/Subscription/SubscriptionHelpScreen';
import { OrganizationDetailScreen } from '@shared/screens/Organization/OrganizationDetailScreen';
import { OrganizationSettingsScreen } from '@shared/screens/Organization/OrganizationSettingsScreen';
import { CreateOrganizationScreen } from '@shared/screens/Organization/CreateOrganizationScreen';
import { JoinOrganizationScreen } from '@shared/screens/Organization/JoinOrganizationScreen';

export function renderHomeStack(entry: TabStackEntry) {
  switch (entry.screen) {
    case 'home':
      return <HomeScreen />;
    case 'assistant-detail':
      return <AssistantDetailScreen />;
    case 'events':
      return <EventListScreen />;
    case 'event-detail':
      return <EventDetailScreen />;
    case 'calendar':
      return <CalendarScreen />;
    case 'attendance':
      return <AttendanceScreen />;
    case 'materials':
      return <MaterialsScreen assistantId={entry.params?.assistantId as string} />;
    default:
      return <HomeScreen />;
  }
}

export function renderAthletesStack(entry: TabStackEntry) {
  switch (entry.screen) {
    case 'athlete-list':
      return <AthletesScreen />;
    case 'athlete-detail':
      return <AthleteDetailScreen />;
    case 'import-athletes':
      return <ImportAthletesScreen />;
    case 'subscription-manage':
      return <SubscriptionManageScreen />;
    case 'subscription-plans':
      return <SubscriptionPlansScreen />;
    default:
      return <AthletesScreen />;
  }
}

export function renderGroupsStack(entry: TabStackEntry) {
  switch (entry.screen) {
    case 'group-list':
      return <GroupsScreen />;
    case 'group-detail':
      return <GroupDetailScreen />;
    case 'athlete-detail':
      return <AthleteDetailScreen />;
    case 'coaches':
      return <CoachListScreen />;
    case 'coach-detail':
      return <CoachDetailScreen />;
    case 'calendar':
      return <CalendarScreen />;
    case 'attendance':
      return <AttendanceScreen />;
    case 'subscription-manage':
      return <SubscriptionManageScreen />;
    case 'subscription-plans':
      return <SubscriptionPlansScreen />;
    default:
      return <GroupsScreen />;
  }
}

export function renderProfileStack(entry: TabStackEntry) {
  switch (entry.screen) {
    case 'profile':
      return <ProfileScreen />;
    case 'directions':
      return <ProfileScreen />;
    case 'feedback':
      return <FeedbackScreen />;
    case 'subscription-manage':
      return <SubscriptionManageScreen />;
    case 'subscription-plans':
      return <SubscriptionPlansScreen />;
    case 'tier-change-confirm':
      return (
        <TierChangeConfirmScreen
          newTierId={entry.params.newTierId as string}
          autoRenewal={entry.params.autoRenewal as boolean | undefined}
          isRenewal={entry.params.isRenewal as boolean | undefined}
        />
      );
    case 'subscription-help':
      return <SubscriptionHelpScreen />;
    case 'organization-detail':
      return <OrganizationDetailScreen />;
    case 'organization-settings':
      return <OrganizationSettingsScreen />;
    case 'create-organization':
      return <CreateOrganizationScreen />;
    case 'join-organization':
      return <JoinOrganizationScreen />;
    default:
      return <ProfileScreen />;
  }
}
